import { cn } from "@/lib/utils";

export const CATEGORIES = [
  { key: "brand_dna", label: "Brand DNA", color: "#4A4A4A" },
  { key: "performance", label: "Performance", color: "#7A7A6E" },
  { key: "creative", label: "Creative", color: "#A89580" },
  { key: "competitor", label: "Competitor", color: "#C4B09A" },
  { key: "decision", label: "Decision", color: "#8E7E6E" },
  { key: "user_pin", label: "Pinned", color: "#5E5E58" },
] as const;

export const CATEGORY_COLORS: Record<string, string> = {
  brand_dna: "#4A4A4A",
  performance: "#7A7A6E",
  creative: "#A89580",
  competitor: "#C4B09A",
  decision: "#8E7E6E",
  user_pin: "#5E5E58",
};

export function getCategoryLabel(cat: string): string {
  return CATEGORIES.find(c => c.key === cat)?.label ?? cat;
}

interface CategoryChipsProps {
  selected: string[];
  onChange: (selected: string[]) => void;
  counts?: Record<string, number>;
}

export function CategoryChips({ selected, onChange, counts }: CategoryChipsProps) {
  const allSelected = selected.length === 0;

  const toggle = (key: string) => {
    if (selected.includes(key)) {
      onChange(selected.filter(k => k !== key));
    } else {
      onChange([...selected, key]);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="category-chips">
      <button
        onClick={() => onChange([])}
        className={cn(
          "px-2.5 py-1 rounded-full text-xs font-medium transition-all border",
          allSelected
            ? "bg-gray-900 text-white border-gray-900"
            : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
        )}
        data-testid="category-chip-all"
      >
        All
      </button>
      {CATEGORIES.map(cat => {
        const isActive = selected.includes(cat.key);
        const count = counts?.[cat.key] ?? 0;
        return (
          <button
            key={cat.key}
            onClick={() => toggle(cat.key)}
            className={cn(
              "px-2.5 py-1 rounded-full text-xs font-medium transition-all border flex items-center gap-1.5",
              isActive
                ? "text-white border-transparent"
                : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
            )}
            style={isActive ? { backgroundColor: cat.color, borderColor: cat.color } : {}}
            data-testid={`category-chip-${cat.key}`}
          >
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: isActive ? "white" : cat.color }} />
            {cat.label}
            {count > 0 && <span className="opacity-75">{count}</span>}
          </button>
        );
      })}
    </div>
  );
}
